/**
 * TalkFeed — a persistent, stateful Talk-tab feed (newest-at-bottom). Created once
 * and reused across app re-renders (like NowMapController), so scroll position and
 * unread state survive. It owns a scrollable container and repaints only a ≤WINDOW
 * row tail, coalesced via requestAnimationFrame, so a burst of incoming messages
 * costs O(WINDOW), not O(all).
 *
 * One state drives everything: `lastSeenId` — the newest message the user has seen.
 *  - unread   = messages after lastSeenId
 *  - divider  = an <hr> before the first unread row
 *  - pill     = "N new ↓", shown while not following
 * `follow` (am I pinned to the bottom?) decides whether a new message auto-scrolls
 * and advances lastSeenId. "reset" mode catches up to newest + follows; "preserve"
 * mode (returning to the tab) anchors at lastSeenId so the gap reads as unread.
 */
import type { MessageEntry } from "./message-model.js";
import { messageRow, type T } from "./talk-row.js";
import { checkingLine, stalledLine, loadStateOf } from "../zone-checking.js";
import type { ZoneLoadState, ZoneLoadProgress } from "../../app/zone-load-gate.js";

export type FeedMode = "reset" | "preserve";

const WINDOW = 50; // rows rendered initially / revealed per scroll-to-top
const BOTTOM_PX = 40; // within this many px of the bottom counts as "at bottom"

export class TalkFeed {
  /** Wrapper inserted into the tab; holds the scroller + the overlay pill. */
  readonly element: HTMLElement;
  #scroller: HTMLElement;
  #rows: HTMLElement;
  #pill: HTMLButtonElement;
  #t: T;

  #list: MessageEntry[] = [];
  #me = "";
  #nowMs = 0;
  #loadState: ZoneLoadState = "loaded";
  #lastSeenId: string | null = null;
  #windowStart = 0;
  #follow = true;
  /** Set before a programmatic scroll so the scroll handler ignores the echo. */
  #programmatic = false;
  #rafPending = false;

  #onRetry?: () => void;
  #progress: ZoneLoadProgress | undefined;
  constructor(opts: { t: T; onRetry?: () => void }) {
    this.#t = opts.t;
    this.#onRetry = opts.onRetry;
    this.element = document.createElement("div");
    this.element.className = "talk-feed-wrap";

    this.#scroller = document.createElement("div");
    this.#scroller.className = "talk-feed";
    this.#rows = document.createElement("div");
    this.#rows.className = "talk-feed__rows";
    this.#scroller.append(this.#rows);

    this.#pill = document.createElement("button");
    this.#pill.type = "button";
    this.#pill.className = "talk-feed__new";
    this.#pill.hidden = true;
    this.#pill.setAttribute("data-action", "jump-newest");
    this.#pill.addEventListener("click", () => this.#jumpToNewest());

    this.element.append(this.#scroller, this.#pill);
    this.#scroller.addEventListener("scroll", () => this.#onScroll());
  }

  /** Bind the current scope-filtered list for a deliberate (re)render.
   *  "reset": catch up to newest and follow. "preserve": keep lastSeenId and
   *  anchor (incoming gap reads as unread). Preserve with nothing seen ⇒ reset. */
  render(list: MessageEntry[], me: string, nowMs: number, mode: FeedMode, loaded: boolean | ZoneLoadState = true, progress?: ZoneLoadProgress): void {
    this.#list = list;
    this.#progress = progress;
    this.#me = me;
    this.#nowMs = nowMs;
    this.#loadState = typeof loaded === "boolean" ? loadStateOf(undefined, loaded) : loaded;
    if (mode === "reset" || this.#lastSeenId === null) {
      this.#lastSeenId = this.#newestId();
      this.#follow = true;
    } else {
      this.#follow = false; // anchor on return
    }
    this.#windowStart = Math.max(0, list.length - WINDOW);
    this.#paint();
    this.#updatePill();
  }

  /** A live message (or burst) was added to the store. Coalesced to one repaint. */
  ingest(list: MessageEntry[], me: string, nowMs: number): void {
    this.#list = list;
    this.#me = me;
    this.#nowMs = nowMs;
    if (this.#rafPending) return;
    this.#rafPending = true;
    requestAnimationFrame(() => {
      this.#rafPending = false;
      if (this.#follow) {
        this.#lastSeenId = this.#newestId();
        this.#windowStart = Math.max(0, this.#list.length - WINDOW);
        this.#paint();
        this.#scrollToBottom();
      } else {
        // Keep the user's window anchored: windowStart unchanged, so the rows above
        // the fold stay (near) identical and scrollTop remains valid. A first-time
        // divider insertion may nudge content by an <hr>'s height — negligible.
        this.#paint();
      }
      this.#updatePill();
    });
  }

  /** Reapply intended scroll after the shell re-attaches `element` (mount can reset
   *  native scrollTop). */
  afterMount(): void {
    if (this.#follow) this.#scrollToBottom();
    else this.#anchorToLastSeen();
  }

  // --- internals ---------------------------------------------------------

  #newestId(): string | null {
    return this.#list.length ? this.#list[this.#list.length - 1].id : null;
  }

  #lastSeenIndex(): number {
    if (this.#lastSeenId === null) return -1;
    return this.#list.findIndex((m) => m.id === this.#lastSeenId);
  }

  /** Number of messages newer than lastSeenId. A seen id evicted by the ring cap
   *  (no longer present) ⇒ treat everything still buffered as unread. */
  #unreadCount(): number {
    if (this.#lastSeenId === null) return 0;
    const i = this.#lastSeenIndex();
    if (i < 0) return this.#list.length;
    return this.#list.length - 1 - i;
  }

  /** Index of the first unread row, or list.length when fully caught up. */
  #firstUnreadIndex(): number {
    if (this.#lastSeenId === null) return this.#list.length;
    const i = this.#lastSeenIndex();
    return i < 0 ? 0 : i + 1;
  }

  #paint(): void {
    this.#rows.replaceChildren();
    if (this.#list.length === 0) {
      if (this.#loadState === "checking") {
        this.#rows.append(checkingLine(this.#t, this.#progress));
      } else if (this.#loadState === "stalled") {
        this.#rows.append(stalledLine(this.#t, this.#onRetry));
      } else {
        const empty = document.createElement("p");
        empty.className = "bx-body talk-feed__empty";
        empty.textContent = this.#t("talk.empty");
        this.#rows.append(empty);
      }
      return;
    }
    const firstUnread = this.#firstUnreadIndex();
    for (let i = this.#windowStart; i < this.#list.length; i++) {
      if (i === firstUnread && firstUnread < this.#list.length) {
        const hr = document.createElement("hr");
        hr.className = "talk-feed__divider";
        hr.setAttribute("data-divider", "");
        this.#rows.append(hr);
      }
      this.#rows.append(messageRow(this.#t, this.#list[i], this.#me, this.#nowMs));
    }
  }

  #updatePill(): void {
    const n = this.#unreadCount();
    if (n > 0 && !this.#follow) {
      this.#pill.hidden = false;
      this.#pill.textContent = this.#t("talk.new.count", { n });
    } else {
      this.#pill.hidden = true;
    }
  }

  #jumpToNewest(): void {
    this.#follow = true;
    this.#lastSeenId = this.#newestId();
    this.#windowStart = Math.max(0, this.#list.length - WINDOW);
    this.#paint();
    this.#scrollToBottom();
    this.#updatePill();
  }

  #scrollToBottom(): void {
    const target = this.#scroller.scrollHeight;
    if (this.#scroller.scrollTop === target) return; // unchanged ⇒ no scroll event fires; don't arm the guard
    this.#programmatic = true;
    this.#scroller.scrollTop = target;
  }

  #anchorToLastSeen(): void {
    const i = this.#lastSeenIndex();
    if (i < 0) { this.#scrollToBottom(); return; }
    const row = this.#rowEl(this.#list[i].id);
    if (!row) { this.#scrollToBottom(); return; }
    const target = row.offsetTop + row.offsetHeight - this.#scroller.clientHeight;
    if (this.#scroller.scrollTop === target) return; // unchanged ⇒ no event; don't arm the guard
    this.#programmatic = true;
    this.#scroller.scrollTop = target;
  }

  #rowEl(id: string): HTMLElement | null {
    // Index loop, not for-of: HTMLCollection isn't iterable under the project's TS lib.
    const kids = this.#rows.children;
    for (let i = 0; i < kids.length; i++) {
      const el = kids[i];
      if (el instanceof HTMLElement && el.getAttribute("data-msg") === id) return el;
    }
    return null;
  }

  #onScroll(): void {
    if (this.#programmatic) { this.#programmatic = false; return; }
    const s = this.#scroller;
    const atBottom = s.scrollHeight - s.scrollTop - s.clientHeight <= BOTTOM_PX;
    this.#follow = atBottom;
    if (atBottom) {
      this.#lastSeenId = this.#newestId();
      this.#paint();
      this.#scrollToBottom();
      this.#updatePill();
    } else if (s.scrollTop <= 0 && this.#windowStart > 0) {
      this.#revealOlder();
    }
  }

  #revealOlder(): void {
    const before = this.#scroller.scrollHeight;
    this.#windowStart = Math.max(0, this.#windowStart - WINDOW);
    this.#paint();
    const added = this.#scroller.scrollHeight - before;
    if (added === 0) return; // nothing prepended height-wise ⇒ no scroll change to compensate or guard
    this.#programmatic = true;
    this.#scroller.scrollTop = this.#scroller.scrollTop + added; // keep the view stable
  }
}
